/**
 * ExpressionBuilderErrorBoundary — the thing that makes "every failure degrades to *the trigger* is
 * unavailable, never *the field* is unavailable" actually true.
 *
 * ── The bug this exists to fix ─────────────────────────────────────────────────────────────────
 *
 * `ExpressionBuilderTrigger` is reached through `React.lazy`, and `Suspense` catches only the
 * *pending* state of that import — a **rejected** import promise is thrown during render like any
 * other error. Without a boundary it unwinds past `FormField` entirely and is caught by
 * `FormRenderer`'s `FieldErrorBoundary`, which replaces the whole field — label, input, value and
 * all — with a generic "Field error" message. A network blip fetching a chunk that exists only to
 * add an optional convenience button would take the user's data entry with it.
 *
 * So: catch it here, one level in, and render the same disabled button the unconfigured-host branch
 * already renders. The native `<input>`/`<textarea>` beside it never learns anything went wrong.
 *
 * ── Why it is deliberately narrow ──────────────────────────────────────────────────────────────
 *
 * It wraps *only* the lazy trigger. It is not a general-purpose boundary and must not become one:
 * swallowing render errors from the field itself is exactly the failure mode `FieldErrorBoundary`
 * exists to surface, and hiding those behind a button would be strictly worse than the bug above.
 *
 * `@atlas-forms/controls-advanced-react` has its own copy of this component for Option B's
 * `ExpressionControl`. The two packages are siblings with no dependency between them — the same
 * deliberate split Plan/05 §3B makes for `configureExpressionBuilder`/`configureExpressionControl`.
 */

import React from 'react';
import { TriggerButton, getTriggerStyle, BUTTON_LABEL } from './TriggerButton';
import { isDevBuild } from './expressionBuilderRuntime';

export interface ExpressionBuilderErrorBoundaryProps {
  /** Rendered on the fallback button so it stays addressable by the field it belongs to. */
  controlID: string;
  /** Matches the surrounding field chrome, exactly as the live trigger's does. */
  theme?: 'light' | 'dark';
  children: React.ReactNode;
}

interface ExpressionBuilderErrorBoundaryState {
  failed: boolean;
}

export class ExpressionBuilderErrorBoundary extends React.Component<
  ExpressionBuilderErrorBoundaryProps,
  ExpressionBuilderErrorBoundaryState
> {
  state: ExpressionBuilderErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ExpressionBuilderErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    // Not silent: a chunk that never loads is a real deployment problem worth seeing in a log, even
    // though the user-visible consequence is only a disabled button.
    //
    // `isDevBuild()` rather than an inline `NODE_ENV` check: the inline `(globalThis as any)
    // .process?.env?.NODE_ENV` form this used to carry contains no token a bundler substitutes and
    // evaluates to `true` in every browser build, so this "dev-only" warning also fired in
    // production. See `isDevBuild` for the full explanation.
    if (isDevBuild()) {
      console.warn(
        '[ExpressionBuilder] The Expression Builder could not be loaded; the trigger is disabled. ' +
          'The field itself is unaffected and remains editable.',
        error,
      );
    }
  }

  render(): React.ReactNode {
    if (!this.state.failed) return this.props.children;

    const theme = this.props.theme ?? 'light';
    return (
      <TriggerButton
        disabled
        state="unavailable"
        style={getTriggerStyle(theme, true)}
        controlID={this.props.controlID}
        title={
          `${BUTTON_LABEL} is unavailable: the Expression Builder could not be loaded. ` +
          'You can still type in this field normally.'
        }
      />
    );
  }
}

export default ExpressionBuilderErrorBoundary;
